// ===============================================================
// V·IANet Requests (M16) — CadenasTab: Editor de cadenas DAG
// Lista pasos + transiciones, add/edit/delete
// ===============================================================

import { useState, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { CadenaAprobacion, CadenaPaso, TipoSolicitud } from '../types/requests.types';
import { request as apiFetch } from '../../../shared/services/api';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';

const TIPOS_RESOLUCION = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'JERARQUICO', label: 'Jerarquico' },
  { value: 'ROL_GOBERNANZA', label: 'Rol de gobernanza' },
  { value: 'UMBRAL_IMPORTE', label: 'Umbral de importe' },
];

interface PasoForm {
  nombre: string;
  orden: number;
  tipoAprobacion: string;
  requiereTodos: boolean;
}

const EMPTY_PASO: PasoForm = {
  nombre: '',
  orden: 1,
  tipoAprobacion: 'MANUAL',
  requiereTodos: false,
};

export function CadenasTab() {
  const { t } = useI18n();
  const {
    items: cadenas,
    loading,
    crear,
    actualizar,
    eliminar,
  } = useRequestsConfig<CadenaAprobacion>('/requests/cadenas');
  const { items: tipos } = useRequestsConfig<TipoSolicitud>('/requests/tipos');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pasos, setPasos] = useState<CadenaPaso[]>([]);
  const [loadingPasos, setLoadingPasos] = useState(false);

  // Cadena CRUD
  const [showCadenaModal, setShowCadenaModal] = useState(false);
  const [cadenaEditId, setCadenaEditId] = useState<string | null>(null);
  const [cadenaForm, setCadenaForm] = useState({
    nombre: '',
    descripcion: '',
    tipoSolicitudId: '',
  });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Paso CRUD
  const [showPasoModal, setShowPasoModal] = useState(false);
  const [pasoEditId, setPasoEditId] = useState<string | null>(null);
  const [pasoForm, setPasoForm] = useState<PasoForm>(EMPTY_PASO);

  const tipoName = (tipoId: string) => tipos.find((t) => t.id === tipoId)?.nombre ?? tipoId;

  const fetchPasos = useCallback(async (cadenaId: string) => {
    setLoadingPasos(true);
    try {
      const res = await apiFetch<{ data: CadenaPaso[] }>(`/requests/cadenas/${cadenaId}/pasos`);
      setPasos(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPasos([]);
    } finally {
      setLoadingPasos(false);
    }
  }, []);

  const selectCadena = (c: CadenaAprobacion) => {
    setSelectedId(c.id);
    if (c.pasos && c.pasos.length > 0) {
      setPasos(c.pasos);
    } else {
      fetchPasos(c.id);
    }
  };

  // ─── Cadena modal ───
  const openCreateCadena = () => {
    setCadenaEditId(null);
    setCadenaForm({ nombre: '', descripcion: '', tipoSolicitudId: tipos[0]?.id ?? '' });
    setShowCadenaModal(true);
  };

  const openEditCadena = (c: CadenaAprobacion) => {
    setCadenaEditId(c.id);
    setCadenaForm({
      nombre: c.nombre,
      descripcion: c.descripcion ?? '',
      tipoSolicitudId: c.tipoSolicitudId,
    });
    setShowCadenaModal(true);
  };

  const handleSaveCadena = async () => {
    try {
      if (cadenaEditId) {
        await actualizar(cadenaEditId, cadenaForm as Partial<CadenaAprobacion>);
      } else {
        await crear(cadenaForm as Partial<CadenaAprobacion>);
      }
      setShowCadenaModal(false);
    } catch {
      // error
    }
  };

  const handleDeleteCadena = async () => {
    if (!confirmDeleteId) return;
    try {
      await eliminar(confirmDeleteId);
      if (selectedId === confirmDeleteId) {
        setSelectedId(null);
        setPasos([]);
      }
    } catch {
      // error
    }
    setConfirmDeleteId(null);
  };

  // ─── Paso modal ───
  const openAddPaso = () => {
    setPasoEditId(null);
    setPasoForm({ ...EMPTY_PASO, orden: pasos.length + 1 });
    setShowPasoModal(true);
  };

  const openEditPaso = (p: CadenaPaso) => {
    setPasoEditId(p.id);
    setPasoForm({
      nombre: p.nombre,
      orden: p.orden,
      tipoAprobacion: p.tipoAprobacion,
      requiereTodos: p.requiereTodos,
    });
    setShowPasoModal(true);
  };

  const handleSavePaso = async () => {
    if (!selectedId) return;
    try {
      const payload = { ...pasoForm, cadenaId: selectedId };
      if (pasoEditId) {
        await apiFetch(`/requests/cadenas/${selectedId}/pasos/${pasoEditId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/requests/cadenas/${selectedId}/pasos`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      await fetchPasos(selectedId);
      setShowPasoModal(false);
    } catch {
      // error
    }
  };

  const handleDeletePaso = async (pasoId: string) => {
    if (!selectedId) return;
    try {
      await apiFetch(`/requests/cadenas/${selectedId}/pasos/${pasoId}`, {
        method: 'DELETE',
      });
      await fetchPasos(selectedId);
    } catch {
      // error
    }
  };

  if (loading) {
    return <LoadingSpinner size="md" muted />;
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.requests?.admin?.cadenas ?? 'Cadenas de Aprobacion'}
        </h3>
        {!selectedId && (
          <button
            onClick={openCreateCadena}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#1F3864',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t.requests?.nuevo ?? 'Nueva cadena'}
          </button>
        )}
      </div>

      {!selectedId ? (
        /* Cadenas list */
        <div>
          {cadenas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
              {t.requests?.sinDatos ?? 'No hay cadenas configuradas'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                  <th style={TH_LEFT}>Nombre</th>
                  <th style={TH_LEFT}>Tipo solicitud</th>
                  <th style={TH_LEFT}>Descripcion</th>
                  <th style={TH_CENTER}>Activo</th>
                  <th style={TH_RIGHT}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cadenas.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={TD}>
                      <button
                        onClick={() => selectCadena(c)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#2F5496',
                          fontWeight: 600,
                          fontSize: 14,
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        {c.nombre}
                      </button>
                    </td>
                    <td style={TD}>{tipoName(c.tipoSolicitudId)}</td>
                    <td style={{ ...TD, color: '#6B7280' }}>{c.descripcion ?? '-'}</td>
                    <td style={TD_CENTER}>
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: c.activo ? '#F0FDF4' : '#FEF2F2',
                          color: c.activo ? '#16A34A' : '#DC2626',
                          fontWeight: 600,
                        }}
                      >
                        {c.activo ? 'Si' : 'No'}
                      </span>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <button onClick={() => openEditCadena(c)} title="Editar" style={ICON_BTN}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                            stroke="#2F5496"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(c.id)}
                        title="Eliminar"
                        style={ICON_BTN}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                            stroke="#DC2626"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        /* Pasos detail view */
        <div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <button
              onClick={() => {
                setSelectedId(null);
                setPasos([]);
              }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              &larr; Volver
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', flex: 1 }}>
              {cadenas.find((c) => c.id === selectedId)?.nombre ?? 'Cadena'}
            </span>
            <button
              onClick={openAddPaso}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                background: '#2F5496',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              + Paso
            </button>
          </div>

          {loadingPasos ? (
            <LoadingSpinner size="sm" muted />
          ) : pasos.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 48,
                color: '#9CA3AF',
                background: '#FAFBFC',
                borderRadius: 10,
                border: '1px dashed #D1D5DB',
              }}
            >
              Sin pasos. Pulsa "+ Paso" para agregar el primer paso de la cadena.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[...pasos]
                .sort((a, b) => a.orden - b.orden)
                .map((p, i, arr) => (
                  <div key={p.id}>
                    {/* Step card */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 16,
                        background: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: 10,
                      }}
                    >
                      {/* Order badge */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: '#1F3864',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {p.orden}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                          {p.nombre}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#6B7280',
                            display: 'flex',
                            gap: 12,
                            marginTop: 2,
                          }}
                        >
                          <span>
                            Tipo:{' '}
                            <strong>
                              {TIPOS_RESOLUCION.find((tr) => tr.value === p.tipoAprobacion)
                                ?.label ?? p.tipoAprobacion}
                            </strong>
                          </span>
                          <span>
                            Requiere todos: <strong>{p.requiereTodos ? 'Si' : 'No'}</strong>
                          </span>
                        </div>
                      </div>
                      <button onClick={() => openEditPaso(p)} title="Editar" style={ICON_BTN}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                            stroke="#2F5496"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeletePaso(p.id)}
                        title="Eliminar"
                        style={ICON_BTN}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                            stroke="#DC2626"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    {/* Arrow between steps */}
                    {i < arr.length - 1 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '4px 0',
                        }}
                      >
                        <svg
                          width="16"
                          height="24"
                          viewBox="0 0 16 24"
                          fill="none"
                          stroke="#9CA3AF"
                          strokeWidth="2"
                        >
                          <line x1="8" y1="0" x2="8" y2="18" />
                          <polyline points="4 14 8 20 12 14" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Cadena Create/Edit Modal */}
      {showCadenaModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {cadenaEditId ? 'Editar cadena' : 'Nueva cadena'}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={LABEL}>
                Nombre *
                <input
                  value={cadenaForm.nombre}
                  onChange={(e) => setCadenaForm((f) => ({ ...f, nombre: e.target.value }))}
                  style={INPUT}
                />
              </label>
              <label style={LABEL}>
                Tipo solicitud
                <select
                  value={cadenaForm.tipoSolicitudId}
                  onChange={(e) =>
                    setCadenaForm((f) => ({ ...f, tipoSolicitudId: e.target.value }))
                  }
                  style={{ ...INPUT, cursor: 'pointer' }}
                >
                  <option value="">— Seleccionar —</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label style={LABEL}>
                Descripcion
                <textarea
                  value={cadenaForm.descripcion}
                  onChange={(e) => setCadenaForm((f) => ({ ...f, descripcion: e.target.value }))}
                  rows={3}
                  style={{ ...INPUT, resize: 'vertical' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={() => setShowCadenaModal(false)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCadena}
                disabled={!cadenaForm.nombre.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: cadenaForm.nombre.trim() ? '#1F3864' : '#9CA3AF',
                  color: '#fff',
                  cursor: cadenaForm.nombre.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paso Create/Edit Modal */}
      {showPasoModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {pasoEditId ? 'Editar paso' : 'Nuevo paso'}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={LABEL}>
                Nombre *
                <input
                  value={pasoForm.nombre}
                  onChange={(e) => setPasoForm((f) => ({ ...f, nombre: e.target.value }))}
                  style={INPUT}
                />
              </label>
              <label style={LABEL}>
                Orden
                <input
                  type="number"
                  value={pasoForm.orden}
                  onChange={(e) => setPasoForm((f) => ({ ...f, orden: Number(e.target.value) }))}
                  min={1}
                  style={INPUT}
                />
              </label>
              <label style={LABEL}>
                Tipo de resolucion
                <select
                  value={pasoForm.tipoAprobacion}
                  onChange={(e) => setPasoForm((f) => ({ ...f, tipoAprobacion: e.target.value }))}
                  style={{ ...INPUT, cursor: 'pointer' }}
                >
                  {TIPOS_RESOLUCION.map((tr) => (
                    <option key={tr.value} value={tr.value}>
                      {tr.label}
                    </option>
                  ))}
                </select>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={pasoForm.requiereTodos}
                  onChange={(e) => setPasoForm((f) => ({ ...f, requiereTodos: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#2F5496' }}
                />
                Requiere todos los aprobadores
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={() => setShowPasoModal(false)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePaso}
                disabled={!pasoForm.nombre.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: pasoForm.nombre.trim() ? '#1F3864' : '#9CA3AF',
                  color: '#fff',
                  cursor: pasoForm.nombre.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 400 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              Confirmar eliminacion
            </h4>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6B7280' }}>
              Se eliminara la cadena y todos sus pasos. Esta accion no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteCadena}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TH_LEFT: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 13,
  fontWeight: 600,
  color: '#6B7280',
};

const TH_CENTER: React.CSSProperties = { ...TH_LEFT, textAlign: 'center' };
const TH_RIGHT: React.CSSProperties = { ...TH_LEFT, textAlign: 'right' };

const TD: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 14,
};

const TD_CENTER: React.CSSProperties = { ...TD, textAlign: 'center' };

const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  display: 'block',
};

const INPUT: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #D1D5DB',
  fontSize: 14,
  boxSizing: 'border-box',
};

const ICON_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  marginLeft: 4,
};
